"use client";

import { useEffect } from "react";

import { analytics } from "@/lib/analytics";
import { buildMenuCtaTrackingProperties } from "@/lib/menu-cta-tracking";

/**
 * Delegated [data-track] click consumer for the /menu surface.
 *
 * The only other delegation (HomepageAnalytics) is scoped to [data-homepage],
 * so menu CTAs (sticky quick-action bar, item-dialog Reserve/Order buttons,
 * directions picker) would otherwise never report. Events flow through
 * src/lib/analytics.ts, which forwards to PostHog and the host's Meta pixel
 * (directions_clicked maps to the FindLocation standard event; the rest reach
 * the pixel as custom events usable for Ads Manager custom conversions).
 *
 * Elements inside a [data-analytics-self-handled] container are skipped: those
 * components call analytics.* directly in their own onClick handlers, and the
 * delegation firing too would double-count.
 */
export function MenuCtaAnalytics() {
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (!(event.target instanceof Element)) return;
      const target = event.target.closest<HTMLElement>("[data-track]");
      if (!target || target.closest("[data-analytics-self-handled]")) return;

      const track = target.dataset.track;
      const location = target.dataset.trackLocation ?? "menu";
      const label =
        target.dataset.trackLabel ??
        target.textContent?.trim().replace(/\s+/g, "_") ??
        track ??
        "unknown";
      const destinationUrl =
        target instanceof HTMLAnchorElement
          ? target.href
          : target.getAttribute("href") ?? undefined;
      const trackingProperties = buildMenuCtaTrackingProperties({
        location,
        label,
        ctaText: target.textContent,
        destinationUrl,
        currentUrl: window.location.href,
      });

      switch (track) {
        case "reservation_clicked":
          analytics.reservationClicked(location, trackingProperties);
          break;
        case "order_started":
          analytics.orderStarted({
            ...trackingProperties,
            source: location,
            order_target: trackingProperties.is_external
              ? trackingProperties.platform
              : undefined,
          });
          break;
        case "directions_clicked":
          analytics.directionsClicked(trackingProperties);
          break;
        case "phone_clicked":
          analytics.phoneClicked(trackingProperties);
          break;
        default:
          if (track) {
            analytics.track(track, {
              ...trackingProperties,
              category: "engagement",
            });
          }
      }
    }

    document.addEventListener("click", handleClick, { capture: true });
    return () =>
      document.removeEventListener("click", handleClick, { capture: true });
  }, []);

  return null;
}

export default MenuCtaAnalytics;
